import { useEffect } from 'react'

import { File, Image } from '@tribeplatform/gql-client/types'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { ImageGalleryView } from '../../../CustomField/components/ImageGallery/ImageGalleryView.js'
import { T } from '../../../i18n/components/T.js'
import { useMediaModal } from '../../../MediaModal/MediaModalProvider.js'

import { MessageAttachment } from './MessageAttachment.js'

interface Props {
  attachments?: File[]
  images?: Image[]
  showAttachmentsCount?: boolean
  isPending?: boolean
}

export const MessageAttachments = ({
  attachments,
  images,
  showAttachmentsCount = true,
  isPending = false,
}: Props) => {
  const { pushSlide, removeSlide } = useMediaModal()

  useEffect(() => {
    const allAttachments = [...(images ?? []), ...(attachments ?? [])]
    allAttachments.forEach(attachment =>
      pushSlide({
        ...attachment,
      }),
    )
    return () => {
      allAttachments.forEach(attachment => removeSlide(attachment.id))
    }
  }, [attachments, images, pushSlide, removeSlide])

  if (!attachments?.length && !images?.length) {
    return null
  }

  const count = attachments?.length + images?.length

  return (
    <div className="flex flex-col w-full max-w-full min-w-[5rem] md:max-w-[22rem] md:w-[22rem]">
      {showAttachmentsCount && (
        <div className="font-light text-xs text-content-subdued whitespace-nowrap flex items-center">
          <T
            id="Generics.Attachments"
            defaultMessage="{count} {count, plural, =1 {file} other {files}}"
            values={{ count }}
          />
          <SvgIcon name="chevron-right" className="w-4 h-4" />
        </div>
      )}
      <div className="flex flex-col gap-2">
        {images?.length > 0 && (
          <div className="min-h-[15rem]">
            {isPending ? (
              <div className="flex justify-center items-center w-full h-full bg-surface-subdued rounded-base"></div>
            ) : (
              <ImageGalleryView
                className="rounded-base overflow-hidden w-full h-full max-w-full max-h-full"
                images={images}
              />
            )}
          </div>
        )}
        {attachments?.length > 0 && (
          <div className="flex flex-col gap-2">
            {attachments.map(attachment => (
              <MessageAttachment key={attachment.id} attachment={attachment} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
